import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { useLotteries } from "../../hooks/useLotteries";
import { usePrize } from "../../hooks/usePrize";
import { PrizeDto } from "../../models/Lottery";
import ChristmasLoader from "../../Components/ChristmasLoader";
import { UploadButton } from "../../Components/UploadButton";

export const PrizeContainer = () => {
  const { prizeId } = useParams<{ prizeId: string }>();
  const navigate = useNavigate();
  const { lotteries } = useLotteries();
  const { loading, addPrize, updatePrize, prize } = usePrize(prizeId);
  const isEditing = !!prizeId && prizeId !== "new";

  const [formData, setFormData] = useState<PrizeDto>({
    name: "",
    description: "",
    quantity: 1,
    lotterySessionId: "",
    id: null,
    image: "",
    drawDateUtc: null,
    drawRound: null,
    imageData: null,
    winningTicketNumber: null,
  });

  useEffect(() => {
    if (!prize) return;

    setFormData(prize);
  }, [prize]);

  if (loading) {
    return <ChristmasLoader />;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    try {
      if (isEditing && prizeId) {
        await updatePrize(formData);
      } else {
        await addPrize(formData);
      }
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "hsl(30, 50%, 98%)", py: 4 }}>
      <Container maxWidth="md">
        <Button
          onClick={() => navigate("/prizes")}
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 3, color: "hsl(150, 60%, 25%)" }}
        >
          πισω στα δωρα
        </Button>

        <Paper
          sx={{
            p: 4,
            borderRadius: 4,
            border: "2px solid hsl(30, 20%, 85%)",
          }}
        >
          <Typography
            variant="h3"
            sx={{
              fontFamily: '"Mountains of Christmas", cursive',
              color: "hsl(45, 90%, 40%)",
              mb: 4,
              fontSize: { xs: "1.8rem", md: "2.2rem" },
            }}
          >
            {isEditing ? "✏️ Επεξεργασία Δώρου" : "🎁 Προσθήκη Νέου Δώρου"}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <TextField
              label="Όνομα Δώρου"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              required
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(45, 90%, 50%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(45, 90%, 40%)",
                },
              }}
            />

            <TextField
              label="Περιγραφή"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              multiline
              rows={3}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(45, 90%, 50%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(45, 90%, 40%)",
                },
              }}
            />
            <TextField
              label="Αριθμός Αναφοράς Δώρου"
              value={formData.referenceNumber}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  referenceNumber: e.target.value,
                }))
              }
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(45, 90%, 50%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(45, 90%, 40%)",
                },
              }}
            />
            <TextField
              label="Ποσότητα"
              type="number"
              value={formData.quantity}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  quantity: Math.max(1, parseInt(e.target.value) || 1),
                }))
              }
              required
              fullWidth
              inputProps={{ min: 1 }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&.Mui-focused fieldset": {
                    borderColor: "hsl(45, 90%, 50%)",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "hsl(45, 90%, 40%)",
                },
              }}
            />

            <FormControl fullWidth required>
              <InputLabel
                sx={{ "&.Mui-focused": { color: "hsl(45, 90%, 40%)" } }}
              >
                Λοταρία
              </InputLabel>
              <Select
                value={formData.lotterySessionId}
                label="Lottery"
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    lotterySessionId: e.target.value,
                  }))
                }
                sx={{
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "hsl(45, 90%, 50%)",
                  },
                }}
              >
                {lotteries.map((lottery: any) => (
                  <MenuItem key={lottery.sessionId} value={lottery.sessionId}>
                    {lottery.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <UploadButton
                saveImage={(image: any) =>
                  setFormData((prev) => ({
                    ...prev,
                    imageData: image,
                  }))
                }
              />
            </FormControl>

            <Button
              type="submit"
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              disabled={!formData.lotterySessionId}
              sx={{
                mt: 2,
                background:
                  "linear-gradient(135deg, hsl(45, 90%, 50%) 0%, hsl(45, 85%, 40%) 100%)",
                color: "hsl(45, 90%, 15%)",
                fontWeight: 600,
                py: 1.5,
                borderRadius: 2,
                "&:hover": {
                  background:
                    "linear-gradient(135deg, hsl(45, 85%, 55%) 0%, hsl(45, 80%, 45%) 100%)",
                },
                "&.Mui-disabled": {
                  background: "hsl(30, 20%, 85%)",
                  color: "hsl(30, 20%, 55%)",
                },
              }}
            >
              {isEditing ? "Αποθηκευση" : "Προσθηκη"}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
